import { useTranslation } from "react-i18next";

import {
  getFocusedItemId,
  HaNContext,
  type HaNFloorSuggestion,
  HaNViewStoreContext,
  selectSelection,
  useHaNView,
  useWorkspacesModel,
} from "@lib/logic";
import type { AreaWithSensorInfo } from "@lib/store";
import { useRequiredContext } from "@lib/utils";
import { EmptyListPlaceholder } from "@molecules";

import { useFitNodeInView } from "../map/useFitNodeInView";
import { FloorSuggestionTile } from "../shared/FloorSuggestionTile";
import { HaNCellSkeleton } from "../shared/HaNCellSkeleton";
import { HaNSlider } from "../shared/HaNSlider/HaNSlider";
import { useOpenResult } from "../shared/useOpenResult";
import { useOpenSuggestedResult } from "../shared/useOpenSuggestedResult";
import { AreaCell } from "./AreaCell";

export const WorkspacesCarousel = () => {
  const { tab } = useRequiredContext(HaNContext);

  if (tab !== "workspaces") return null;

  return <WorkspacesCarouselContent />;
};

// The same areas as the list, then each other floor's tile and its suggestions. It centres on the selected area, or
// the first one.
const WorkspacesCarouselContent = () => {
  const { t } = useTranslation();
  const { isLoading, areas, suggestions } = useWorkspacesModel();
  const viewStore = useRequiredContext(HaNViewStoreContext);
  const selectedId = useHaNView(state => selectSelection("workspaces")(state)?.id ?? null);
  const openResult = useOpenResult();
  const openSuggestedResult = useOpenSuggestedResult();
  const fitNodeInView = useFitNodeInView();

  if (isLoading) {
    return (
      <HaNSlider.Card className="items-center">
        <HaNCellSkeleton />
      </HaNSlider.Card>
    );
  }

  if (areas.length === 0 && !suggestions.above && !suggestions.below) {
    return (
      <HaNSlider.Card>
        <EmptyListPlaceholder text={t("hereAndNow:NoAreasAvailable")} />
      </HaNSlider.Card>
    );
  }

  const areaIds = areas.map(area => area.id);
  const focusedIndex = areaIds.indexOf(getFocusedItemId(areaIds, selectedId) ?? "");

  // Swiping to one of the floor's areas selects it, like tapping it on the map. Other floors aren't on the map.
  const focusSlide = (index: number) => {
    const area = areas[index];

    if (!area) return;

    viewStore.select("workspaces", { kind: "area", id: area.id });

    fitNodeInView(area.id);
  };

  // Swiper only finds slides among its children and fragments, so these are elements rather than a component
  const renderSuggestionSlides = (
    suggestion: HaNFloorSuggestion<AreaWithSensorInfo> | null,
    direction: "above" | "below",
  ) => {
    if (!suggestion) return null;

    return (
      <>
        <HaNSlider.Slide>
          <HaNSlider.Card>
            <FloorSuggestionTile
              floorId={suggestion.floorId}
              direction={direction}
            />
          </HaNSlider.Card>
        </HaNSlider.Slide>

        {suggestion.items.map(area => (
          <HaNSlider.Slide key={area.id}>
            <HaNSlider.CardButton onClick={() => openSuggestedResult("area", area, direction)}>
              <AreaCell area={area} />
            </HaNSlider.CardButton>
          </HaNSlider.Slide>
        ))}
      </>
    );
  };

  return (
    <HaNSlider
      focusedIndex={focusedIndex}
      onFocusChange={focusSlide}>
      {areas.length > 0 ? (
        areas.map(area => (
          <HaNSlider.Slide key={area.id}>
            <HaNSlider.CardButton onClick={() => openResult("area", area)}>
              <AreaCell area={area} />
            </HaNSlider.CardButton>
          </HaNSlider.Slide>
        ))
      ) : (
        <HaNSlider.Slide>
          <HaNSlider.Card>
            <EmptyListPlaceholder
              text={t("hereAndNow:NoAreasAvailable")}
              className="justify-center p-2 [&>svg]:size-14"
            />
          </HaNSlider.Card>
        </HaNSlider.Slide>
      )}
      {renderSuggestionSlides(suggestions.above, "above")}
      {renderSuggestionSlides(suggestions.below, "below")}
    </HaNSlider>
  );
};
